import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Typography, Paper, Grid, Box, Tab, Tabs, 
  Card, CardContent, CardHeader, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  List, ListItem, ListItemText, Button, TextField, Autocomplete
} from '@mui/material';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  PeopleAlt as PeopleIcon,
  Description as PostIcon,
  Favorite as LikeIcon,
  Comment as CommentIcon,
  Bookmark as BookmarkIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Stats.css';
import { apiService } from '../utils/api';
import config from '../config';
import moment from 'moment-timezone';
import 'moment/locale/ja';

// moment の設定
moment.locale('ja');
moment.tz.setDefault('Asia/Tokyo');

// 単純化したStatsコンポーネント
function Stats() {
  // ステート管理
  const [activeTab, setActiveTab] = useState(0);
  const [overviewStats, setOverviewStats] = useState(null);
  const [engagementStats, setEngagementStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [accessInfo, setAccessInfo] = useState(null);
  const [linkedUserStats, setLinkedUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailStats, setUserDetailStats] = useState(null);
  const [loadingUserStats, setLoadingUserStats] = useState(false);
  const { currentUser, logout, refreshToken } = useAuth();
  const navigate = useNavigate();

  // データ取得
  useEffect(() => {
    // すでにロード済みの場合は再取得しない
    if (!loading) return;
    
    const fetchData = async () => {
      try {
        
        setError(null);

        // アクセス権限を取得
        try {
          
          const accessResponse = await apiService.get('/api/stats/', {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            params: {
              _t: Date.now() // キャッシュ回避のためのタイムスタンプ
            }
          });
          setAccessInfo(accessResponse.data);

          // 管理者のみ全体の統計情報を取得
          if (accessResponse.data.user_type === 'admin') {
            try {
              // 基本的な統計情報を取得
              const [overviewRes, engagementRes] = await Promise.all([
                apiService.get('/api/stats/overview', {
                  headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                  },
                  params: {
                    _t: Date.now() // キャッシュ回避のためのタイムスタンプ
                  }
                }),
                apiService.get('/api/stats/posts/engagement', {
                  headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                  },
                  params: {
                    _t: Date.now() // キャッシュ回避のためのタイムスタンプ
                  }
                })
              ]);
              
              // APIレスポンスの構造をチェック
              
              
              
              // dataプロパティがある場合はそれを使用、ない場合はレスポンス自体を使用
              const overviewData = overviewRes.data.data || overviewRes.data;
              const engagementData = engagementRes.data.data || engagementRes.data;
              
              // データ構造の詳細をログ出力
              
              
              
              
              // 統計データをそのまま設定
              setOverviewStats(overviewData);
              setEngagementStats(engagementData);
              
              // ユーザーリストは別途取得（エラーをキャッチして処理）
              try {
                // 通常のユーザーリスト取得を試みる
                const usersRes = await apiService.get('/api/users', {
                  headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                  },
                  params: {
                    _t: Date.now() // キャッシュ回避のためのタイムスタンプ
                  }
                });
                // ユーザーリストを設定
                if (usersRes.data && Array.isArray(usersRes.data.users)) {
                  setAllUsers(usersRes.data.users);
                  
                } else {
                  // ユーザーリストの形式が期待と異なる場合
                  throw new Error('ユーザーデータの形式が不正です');
                }
              } catch (usersError) {
                
                setError('ユーザーリストの取得に失敗しました。管理者に連絡してください。');
                setAllUsers([]);
              }
            } catch (statsError) {
              
              handleError(statsError);
            }
          }

          // ユーザー自身の統計を取得
          if (currentUser?.id) {
            try {
              const userResponse = await apiService.get(`/api/stats/user/${currentUser.id}`, {
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
                },
                params: {
                  _t: Date.now() // キャッシュ回避のためのタイムスタンプ
                }
              });
              
              setUserStats(userResponse.data.data || userResponse.data);
            } catch (userStatsError) {
              
              setError('ユーザー統計情報の取得に失敗しました。');
            }
          }

          // 企業ユーザーの場合、紐づけユーザーの統計を取得
          if (accessResponse.data.user_type === 'corporate' && accessResponse.data.linked_users) {
            const linkedStats = {};
            let hasError = false;
            
            for (const user of accessResponse.data.linked_users) {
              try {
                const response = await apiService.get(`/api/stats/user/${user.user_id}`, {
                  headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                  },
                  params: {
                    _t: Date.now() // キャッシュ回避のためのタイムスタンプ
                  }
                });
                
                linkedStats[user.user_id] = response.data.data || response.data;
              } catch (err) {
                
                hasError = true;
              }
            }
            
            setLinkedUserStats(linkedStats);
            
            if (hasError) {
              setError('一部のユーザー統計情報の取得に失敗しました。');
            }
          }
        } catch (err) {
          handleError(err);
        }

        setLoading(false);
      } catch (err) {
        setError(`統計情報の取得に失敗しました: ${err.message}`);
        setLoading(false);
      }
    };

    fetchData();
  }, []); // 依存配列を空にして初回のみ実行するように変更

  // ユーザー情報が変更された時に統計情報を再取得する
  useEffect(() => {
    if (!loading && currentUser) {
      
      setLoading(true);
    }
  }, [currentUser]);

  // 特定ユーザーの統計情報を取得
  const fetchUserStats = async (userId) => {
    if (!userId) return;
    
    try {
      setLoadingUserStats(true);
      
      
      const response = await apiService.get(`/api/stats/user/${userId}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        }
      });
      const userData = response.data.data || response.data;
      
      
      
      
      setUserDetailStats(userData);
      setLoadingUserStats(false);
    } catch (err) {
      
      setError('ユーザーの統計情報の取得に失敗しました');
      setLoadingUserStats(false);
    }
  };

  // ユーザー選択時の処理
  const handleUserSelect = (event, user) => {
    setSelectedUser(user);
    if (user) {
      fetchUserStats(user.id);
    } else {
      setUserDetailStats(null);
    }
  };

  // エラー処理ヘルパー
  const handleError = (err) => {
    if (err.response) {
      if (err.response.status === 401) {
        setError('認証エラー：セッションの有効期限が切れています。');
        
        // トークン更新エラー時のログアウト防止
        // ローカルストレージの情報を保持したままエラーメッセージのみ表示
        if (refreshToken) {
          
          
          // ページローディング中に表示
          setLoading(true);
          setError('認証情報を更新中...');
          
          refreshToken()
            .then(() => {
              
              // トークン更新成功後、すぐにuseEffectが実行されるようloading状態を設定
              setLoading(true);
              setError(null);
            })
            .catch((refreshErr) => {
              
              // 失敗してもログアウトしない
              setError('認証の更新に失敗しました。再ログインが必要な場合があります。');
              setLoading(false);
            });
        }
      } else if (err.response.status === 403) {
        setError('アクセス権限がありません。管理者に連絡してください。');
      } else {
        setError(`エラー: ${err.response.data?.message || err.message}`);
      }
    } else if (err.request) {
      setError('サーバーに接続できません。ネットワーク接続を確認してください。');
    } else {
      setError(`リクエストエラー: ${err.message}`);
    }
  };

  // データの再取得
  const handleRefresh = () => {
    window.location.reload();
  };

  // ローディング表示
  if (loading) {
    return (
      <Container className="stats-container">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>統計情報を読み込み中...</Typography>
        </Box>
      </Container>
    );
  }

  // エラー表示
  if (error) {
    return (
      <Container className="stats-container">
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              <RefreshIcon /> 再読込
            </Button>
          }
          sx={{ mt: 2 }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // アクセス権限がない場合
  if (!accessInfo) {
    return (
      <Container className="stats-container">
        <Alert 
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              <RefreshIcon /> 再読込
            </Button>
          }
          sx={{ mt: 2 }}
        >
          統計情報へのアクセス権限がありません
        </Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/login')}
          >
            ログイン画面へ
          </Button>
        </Box>
      </Container>
    );
  }

  // 自分の統計情報を表示するコンポーネント
  const renderMyStats = () => {
    if (!userStats) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          ユーザー統計情報を取得できませんでした。
          <Button color="inherit" size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
            <RefreshIcon /> 再読込
          </Button>
        </Alert>
      );
    }

    // ユーザー統計情報の構造が完全でない場合
    if (!userStats.engagement || !userStats.social) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          ユーザー統計データの形式が不正です。管理者に連絡してください。
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption">取得データ構造: {Object.keys(userStats).join(', ')}</Typography>
          </Box>
        </Alert>
      );
    }

    return (
      <Card elevation={3}>
        <CardHeader title={`${userStats.display_name || 'あなた'}の統計`} />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>エンゲージメント</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><PostIcon color="primary" /> 投稿</TableCell>
                        <TableCell align="right">{userStats.engagement.posts}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><LikeIcon color="secondary" /> 受け取ったいいね</TableCell>
                        <TableCell align="right">{userStats.engagement.likes_received}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><CommentIcon color="primary" /> 受け取ったコメント</TableCell>
                        <TableCell align="right">{userStats.engagement.comments_received}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><BookmarkIcon color="secondary" /> 受け取ったブックマーク</TableCell>
                        <TableCell align="right">{userStats.engagement.bookmarks_received}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>ソーシャル</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>フォロー中</TableCell>
                        <TableCell align="right">{userStats.social.following}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>フォロワー</TableCell>
                        <TableCell align="right">{userStats.social.followers}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>アカウント作成日からの日数</TableCell>
                        <TableCell align="right">{userStats.account_age} 日</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // 管理者向け: ユーザー別統計表示
  const renderUserStatsSearch = () => {
    return (
      <>
        <Typography variant="h5" gutterBottom>ユーザー別統計</Typography>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Autocomplete
              sx={{ width: '100%' }}
              options={allUsers}
              getOptionLabel={(option) => `${option.display_name || option.username} (@${option.username})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="ユーザーを検索"
                  placeholder="ユーザー名で検索..."
                  variant="outlined"
                  fullWidth
                />
              )}
              value={selectedUser}
              onChange={handleUserSelect}
            />
          </Box>

          {loadingUserStats && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress size={30} />
            </Box>
          )}

          {userDetailStats && !loadingUserStats && renderUserDetailStats(userDetailStats)}

          {!userDetailStats && !loadingUserStats && selectedUser && (
            <Alert severity="info">統計情報を取得できませんでした。ユーザーが存在しないか、データがありません。</Alert>
          )}

          {!selectedUser && !loadingUserStats && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body1" color="text.secondary">
                ユーザーを選択すると、詳細な統計情報が表示されます
              </Typography>
            </Box>
          )}
        </Paper>
      </>
    );
  };

  // 企業ユーザー向けの紐づけユーザー統計
  const renderLinkedUserStats = () => {
    if (!accessInfo.linked_users || accessInfo.linked_users.length === 0) {
      return <Alert severity="info">紐づけられた投稿ユーザーがいません</Alert>;
    }

    return (
      <Box>
        {accessInfo.linked_users.map((user) => {
          const stats = linkedUserStats[user.user_id];
          if (!stats) {
            return (
              <Alert severity="warning" key={user.user_id} sx={{ mb: 3 }}>
                {user.display_name || user.username}さんの統計情報を取得できませんでした
              </Alert>
            );
          }
          
          return renderUserDetailStats({
            ...stats,
            display_name: user.display_name,
            username: user.username,
            id: user.user_id
          });
        })}
      </Box>
    );
  };

  // 管理者向け: 全体統計の表示
  const renderOverviewStats = () => {
    if (!overviewStats) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          統計データを取得できませんでした。管理者に連絡してください。
          <Button color="inherit" size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
            <RefreshIcon /> 再読込
          </Button>
        </Alert>
      );
    }

    

    // APIレスポンスの構造が期待と異なる場合でも表示できるようにする
    // content_stats, timestamp, trendingは存在するが、user_statsがない場合を考慮
    return (
      <>
        <Typography variant="h5" gutterBottom>全体の統計情報</Typography>
        <Grid container spacing={3}>
          {/* ユーザー統計 - APIレスポンスに含まれる場合のみ表示 */}
          {overviewStats.user_stats && (
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>ユーザー統計</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>総ユーザー数</TableCell>
                        <TableCell align="right">{overviewStats.user_stats.total_users}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>新規ユーザー（30日）</TableCell>
                        <TableCell align="right">{overviewStats.user_stats.new_users}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>アクティブユーザー</TableCell>
                        <TableCell align="right">{overviewStats.user_stats.active_users}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
          
          {/* コンテンツ統計 */}
          {overviewStats.content_stats && (
            <Grid item xs={12} md={overviewStats.user_stats ? 4 : 6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>コンテンツ統計</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>総投稿数</TableCell>
                        <TableCell align="right">{overviewStats.content_stats.total_posts}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>新規投稿（30日）</TableCell>
                        <TableCell align="right">{overviewStats.content_stats.new_posts}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>総いいね数</TableCell>
                        <TableCell align="right">{overviewStats.content_stats.total_likes}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>総コメント数</TableCell>
                        <TableCell align="right">{overviewStats.content_stats.total_comments}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
          
          {/* トレンド情報 */}
          {overviewStats.trending && (
            <Grid item xs={12} md={overviewStats.user_stats ? 4 : 6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>トレンド</Typography>
                <Typography variant="subtitle2" gutterBottom>人気のハッシュタグ</Typography>
                {Array.isArray(overviewStats.trending.popular_hashtags) && 
                 overviewStats.trending.popular_hashtags.length > 0 ? (
                  <List dense>
                    {overviewStats.trending.popular_hashtags.map((tag, index) => (
                      <ListItem key={index}>
                        <ListItemText 
                          primary={`#${tag.tag}`} 
                          secondary={`${tag.count}件の投稿`} 
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    表示できるハッシュタグがありません
                  </Typography>
                )}
              </Paper>
            </Grid>
          )}

          {/* 更新日時の表示 */}
          {overviewStats.timestamp && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'right', mt: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  最終更新: {moment.utc(overviewStats.timestamp).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm')}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  // 管理者向け: エンゲージメント統計の表示
  const renderEngagementStats = () => {
    if (!engagementStats) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          エンゲージメント統計を取得できませんでした。管理者に連絡してください。
          <Button color="inherit" size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
            <RefreshIcon /> 再読込
          </Button>
        </Alert>
      );
    }

    

    // APIからの応答データ構造に合わせて表示
    return (
      <>
        <Typography variant="h5" gutterBottom>投稿エンゲージメント</Typography>
        <Grid container spacing={3}>
          {/* 月間投稿数 */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>月間投稿数の推移</Typography>
              <Box sx={{ height: 300, overflowY: 'auto' }}>
                {engagementStats.monthly_posts && Array.isArray(engagementStats.monthly_posts) && 
                 engagementStats.monthly_posts.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>月</TableCell>
                          <TableCell align="right">投稿数</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...engagementStats.monthly_posts].reverse().map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.month}</TableCell>
                            <TableCell align="right">{item.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body1">データがありません</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* 月間いいね数 */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>月間いいね数の推移</Typography>
              <Box sx={{ height: 300, overflowY: 'auto' }}>
                {engagementStats.monthly_likes && Array.isArray(engagementStats.monthly_likes) && 
                 engagementStats.monthly_likes.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>月</TableCell>
                          <TableCell align="right">いいね数</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...engagementStats.monthly_likes].reverse().map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.month}</TableCell>
                            <TableCell align="right">{item.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body1">データがありません</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* 人気の投稿 */}
          {engagementStats.popular_posts && Array.isArray(engagementStats.popular_posts) && (
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>人気の投稿</Typography>
                {engagementStats.popular_posts.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>投稿者</TableCell>
                          <TableCell>テキスト</TableCell>
                          <TableCell align="right">いいね数</TableCell>
                          <TableCell align="right">投稿日</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {engagementStats.popular_posts.map((post, index) => (
                          <TableRow key={index}>
                            <TableCell>{post.author?.display_name || '不明'}</TableCell>
                            <TableCell>
                              {post.text?.length > 50 ? post.text.substring(0, 50) + '...' : post.text || ''}
                            </TableCell>
                            <TableCell align="right">{post.like_count || 0}</TableCell>
                            <TableCell align="right">
                              {post.created_at ? moment.utc(post.created_at).tz('Asia/Tokyo').format('YYYY/MM/DD') : '不明'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body1">人気の投稿データがありません</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
          
          {/* タイムスタンプ表示 */}
          {engagementStats.timestamp && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'right', mt: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  最終更新: {moment.utc(engagementStats.timestamp).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm')}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  // ユーザー別統計の詳細表示
  const renderUserDetailStats = (stats) => {
    if (!stats) return null;
    
    
    
    // APIレスポンスに合わせて表示
    return (
      <Card elevation={3}>
        <CardHeader 
          title={`${stats.display_name || stats.username || '不明なユーザー'}の統計`}
          subheader={stats.username ? `@${stats.username}` : ''}
        />
        <CardContent>
          <Grid container spacing={3}>
            {/* エンゲージメント情報があれば表示 */}
            {stats.engagement && (
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>エンゲージメント</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><PostIcon color="primary" /> 投稿</TableCell>
                          <TableCell align="right">{stats.engagement.posts || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><LikeIcon color="secondary" /> いいね獲得数</TableCell>
                          <TableCell align="right">{stats.engagement.likes_received || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><CommentIcon color="primary" /> コメント獲得数</TableCell>
                          <TableCell align="right">{stats.engagement.comments_received || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><BookmarkIcon color="secondary" /> ブックマーク獲得数</TableCell>
                          <TableCell align="right">{stats.engagement.bookmarks_received || 0}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
            
            {/* ソーシャル情報があれば表示 */}
            {stats.social && (
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>ソーシャル</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>フォロー中</TableCell>
                          <TableCell align="right">{stats.social.following || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>フォロワー</TableCell>
                          <TableCell align="right">{stats.social.followers || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>アカウント作成日からの日数</TableCell>
                          <TableCell align="right">{stats.account_age || '-'} 日</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
            
            {/* APIレスポンスに他のデータが含まれていれば表示 */}
            {stats.timestamp && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    最終更新: {moment.utc(stats.timestamp).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm')}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // コンテンツの出し分け
  const renderContent = () => {
    const userType = accessInfo?.user_type;
    const hasUsersList = allUsers.length > 0;

    if (userType === 'admin') {
      // ユーザーリストがない場合、タブの調整
      const adjustedTab = !hasUsersList && activeTab > 1 ? activeTab - 1 : activeTab;
      
      
      
      if (adjustedTab === 0) {
        
        return renderOverviewStats();
      } else if (adjustedTab === 1) {
        
        return renderEngagementStats();
      } else if (adjustedTab === 2 && hasUsersList) {
        
        return renderUserStatsSearch();
      } else {
        
        return renderMyStats();
      }
    } else if (userType === 'corporate') {
      
      
      if (activeTab === 0) {
        
        return renderMyStats();
      } else {
        
        return renderLinkedUserStats();
      }
    } else {
      
      return renderMyStats();
    }
  };

  // タブの表示
  const renderTabs = () => {
    const userType = accessInfo?.user_type;
    
    if (userType === 'admin') {
      const tabs = [
        <Tab key="overview" label="全体統計" />,
        <Tab key="engagement" label="投稿エンゲージメント" />
      ];
      
      // ユーザーリストが存在する場合のみユーザー別統計タブを表示
      if (allUsers.length > 0) {
        tabs.push(<Tab key="users" label="ユーザー別統計" />);
      }
      
      tabs.push(<Tab key="mystats" label="自分の統計" />);
      
      return (
        <Tabs 
          value={activeTab < tabs.length ? activeTab : 0} 
          onChange={(e, val) => {
            
            
            setActiveTab(val);
          }} 
          centered
        >
          {tabs}
        </Tabs>
      );
    } else if (userType === 'corporate') {
      return (
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => {
            
            
            setActiveTab(val);
          }} 
          centered
        >
          <Tab label="自分の統計" />
          <Tab label="紐づけユーザーの統計" />
        </Tabs>
      );
    }
    
    return null;
  };

  // 最終的なレンダリング
  return (
    <Container sx={{ mt: 3, mb: 5 }}>
      <Typography variant="h4" gutterBottom align="center">
        統計情報
      </Typography>
      
      {(() => {
        const tabs = renderTabs();
        return tabs ? (
          <Paper elevation={1} sx={{ mb: 3 }}>
            {tabs}
          </Paper>
        ) : null;
      })()}
      
      <Box mt={3}>
        {renderContent()}
      </Box>
    </Container>
  );
}

export default Stats; 